import React, { useEffect, useRef, useState } from "react";
import api from "../api/axios";
import "./SessionAIChatDrawer.css";

export default function SessionAIChatDrawer({
  open,
  onClose,
  sessionId,
  patient,
}) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi, I can help you understand this session safely. Ask about symptoms, red flags, doctor questions, or next discussion points.",
    },
  ]);

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  const [cameraOn, setCameraOn] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const bottomRef = useRef(null);
  const recognitionRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const imageThinkingIntervalRef = useRef(null);

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("authToken");

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [messages, open, imageFile, cameraOn]);

  useEffect(() => {
    if (!cameraOn || !videoRef.current || !cameraStreamRef.current) return;

    const video = videoRef.current;
    video.srcObject = cameraStreamRef.current;

    const playCamera = async () => {
      try {
        await video.play();
      } catch (err) {
        console.error("Video play error:", err);
      }
    };

    playCamera();
  }, [cameraOn]);

  useEffect(() => {
    return () => {
      if (imageThinkingIntervalRef.current) {
        clearInterval(imageThinkingIntervalRef.current);
      }

      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }

      if (videoRef.current) {
        videoRef.current.pause?.();
        videoRef.current.srcObject = null;
      }

      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }

      if (recognitionRef.current) {
        recognitionRef.current.stop?.();
      }
    };
  }, [imagePreviewUrl]);

  const setSelectedImage = (file) => {
    if (!file) return;

    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const removeSelectedImage = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setImageFile(null);
    setImagePreviewUrl("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }

    setSelectedImage(file);
  };

  const stopCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.pause?.();
      videoRef.current.srcObject = null;
    }

    setCameraOn(false);
  };

  const startCamera = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        alert("Camera API is not supported in this browser.");
        return;
      }

      stopCamera();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      cameraStreamRef.current = stream;
      setCameraOn(true);
    } catch (err) {
      console.error("Camera error:", err);

      if (err.name === "NotAllowedError") {
        alert(
          "Camera permission was blocked. Please allow camera access in browser/site settings."
        );
      } else if (err.name === "NotFoundError") {
        alert("No camera device found.");
      } else if (err.name === "NotReadableError") {
        alert("Camera is already in use by another app or tab.");
      } else {
        alert(`Camera not accessible: ${err.name || err.message}`);
      }
    }
  };

  const captureImage = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;

    if (!canvas || !video) return;

    if (video.readyState < 2) {
      alert("Camera is still loading. Please try again in a second.");
      return;
    }

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        const file = new File([blob], "capture.jpg", {
          type: "image/jpeg",
        });

        setSelectedImage(file);
      },
      "image/jpeg",
      0.92
    );

    stopCamera();
  };

  const startVoice = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Voice input is not supported in this browser.");
      return;
    }

    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";
    recognition.interimResults = false;
    recognition.continuous = false;

    recognitionRef.current = recognition;
    setIsRecording(true);

    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || "";

      if (transcript) {
        setInput((prev) => `${prev ? `${prev} ` : ""}${transcript}`);
      }
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event);
      setIsRecording(false);
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    recognition.start();
  };

  const appendAssistantToken = (chunk) => {
    if (!chunk) return;

    setMessages((prev) => {
      const copy = [...prev];
      const lastIndex = copy.length - 1;

      if (lastIndex < 0 || copy[lastIndex].role !== "assistant") {
        return [...copy, { role: "assistant", content: chunk, progress: null }];
      }

      copy[lastIndex] = {
        ...copy[lastIndex],
        content: `${copy[lastIndex].content || ""}${chunk}`,
        progress: null,
      };

      return copy;
    });
  };

  const updateLastAssistantMessage = (content, progress = null) => {
    setMessages((prev) => {
      const copy = [...prev];
      const lastIndex = copy.length - 1;

      if (lastIndex < 0 || copy[lastIndex].role !== "assistant") {
        return [
          ...copy,
          {
            role: "assistant",
            content,
            progress,
          },
        ];
      }

      copy[lastIndex] = {
        ...copy[lastIndex],
        content,
        progress,
      };

      return copy;
    });
  };

  const startImageThinkingAnimation = () => {
    const states = [
      "Uploading image...",
      "Optimizing image for CPU...",
      "Waiting for image model...",
      "Analyzing visible findings...",
      "Preparing medical notes...",
    ];

    let index = 0;

    if (imageThinkingIntervalRef.current) {
      clearInterval(imageThinkingIntervalRef.current);
    }

    imageThinkingIntervalRef.current = setInterval(() => {
      updateLastAssistantMessage(states[index % states.length], null);
      index += 1;
    }, 2500);
  };

  const stopImageThinkingAnimation = () => {
    if (imageThinkingIntervalRef.current) {
      clearInterval(imageThinkingIntervalRef.current);
      imageThinkingIntervalRef.current = null;
    }
  };

  const pollImageResult = async (jobId) => {
    startImageThinkingAnimation();

    while (true) {
      await new Promise((resolve) => setTimeout(resolve, 2500));

      const res = await api.get(`/api/ai/image/status/${jobId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const job = res.data;

      if (job?.message) {
        updateLastAssistantMessage(job.message, job.progress || 0);
      }

      if (job.status === "done") {
        stopImageThinkingAnimation();
        return job.result || "Image analysis completed.";
      }

      if (job.status === "failed") {
        stopImageThinkingAnimation();
        throw new Error(job.error || "Image processing failed");
      }
    }
  };

  const sendImageMessage = async (text) => {
    const formData = new FormData();
    formData.append("prompt", text || "Please analyze this image safely.");
    formData.append("image", imageFile);
    formData.append("session_id", String(sessionId || ""));

    const startRes = await api.post("/api/ai/image/start", formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });

    const jobId = startRes.data?.job_id;

    if (!jobId) {
      throw new Error("Image job was not created");
    }

    return pollImageResult(jobId);
  };

  const sendStreamingTextMessage = async (text) => {
    const baseURL = api.defaults.baseURL || "";

    const response = await fetch(`${baseURL}/api/ai/chat/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        session_id: sessionId,
        message: text,
        max_new_tokens: 800,
      }),
    });

    if (!response.ok || !response.body) {
      throw new Error(`AI chat failed: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");

    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const eventBlocks = buffer.split("\n\n");
      buffer = eventBlocks.pop() || "";

      for (const block of eventBlocks) {
        if (!block.trim()) continue;

        const lines = block.split("\n");
        const eventLine = lines.find((line) => line.startsWith("event:"));
        const dataLines = lines.filter((line) => line.startsWith("data:"));

        const eventName = eventLine
          ? eventLine.replace("event:", "").trim()
          : "message";

        if (eventName === "start") {
          continue;
        }

        if (!dataLines.length) continue;

        const rawData = dataLines
          .map((line) => line.replace("data:", "").trim())
          .join("\n");

        let payload = {};

        try {
          payload = JSON.parse(rawData);
        } catch {
          payload = { text: rawData };
        }

        if (eventName === "token") {
          const tokenText =
            payload.token ||
            payload.text ||
            payload.content ||
            payload.delta ||
            "";

          if (tokenText) {
            appendAssistantToken(tokenText);
          }
        }

        if (eventName === "error") {
          throw new Error(payload.message || "AI error");
        }

        if (eventName === "done") {
          return;
        }
      }
    }
  };

  const sendMessage = async () => {
    const text = input.trim();

    if ((!text && !imageFile) || loading) return;

    if (!token) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Please login again. Your session token was not found.",
        },
      ]);
      return;
    }

    setLoading(true);

    const imageUrlForBubble = imagePreviewUrl;

    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: text || "Please analyze this image.",
        imageUrl: imageUrlForBubble || "",
      },
      {
        role: "assistant",
        content: imageFile ? "Uploading image..." : "",
        progress: imageFile ? 5 : null,
      },
    ]);

    setInput("");

    try {
      if (imageFile) {
        const reply = await sendImageMessage(text);
        removeSelectedImage();

        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = {
            role: "assistant",
            content: reply,
            progress: null,
          };
          return copy;
        });
      } else {
        await sendStreamingTextMessage(text);
      }
    } catch (err) {
      console.error(err);
      stopImageThinkingAnimation();

      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = {
          role: "assistant",
          content:
            err.message ||
            "Image analysis failed. Please try with a smaller or clearer image.",
          progress: null,
        };
        return copy;
      });
    } finally {
      setLoading(false);
      stopImageThinkingAnimation();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const applySuggestion = (text) => {
    if (!loading) setInput(text);
  };

  return (
    <>
      <div
        className={`ai-chat-backdrop ${open ? "show" : ""}`}
        onClick={onClose}
      />

      <aside className={`ai-chat-drawer ${open ? "open" : ""}`}>
        <div className="ai-chat-header">
          <div>
            <p className="ai-chat-label">Session AI Assist</p>
            <h3>Ask Medgen</h3>
            <span>
              {patient?.name ? `${patient.name} · ` : ""}
              Session #{sessionId}
            </span>
          </div>

          <button className="ai-chat-close" onClick={onClose} type="button">
            ×
          </button>
        </div>

        <div className="ai-chat-context">
          <strong>Context-aware chat</strong>
          <small>Uses backend-secured session context</small>
        </div>

        <div className="ai-chat-messages">
          {messages.map((message, index) => (
            <div
              key={`${message.role}-${index}`}
              className={`ai-chat-message ${message.role}`}
            >
              <div className="ai-chat-bubble">
                {message.imageUrl && (
                  <img
                    src={message.imageUrl}
                    alt="Uploaded"
                    className="ai-chat-bubble-image"
                  />
                )}

                {message.content ||
                  (loading && index === messages.length - 1
                    ? "Thinking..."
                    : "")}

                {message.progress !== null &&
                  message.progress !== undefined &&
                  message.role === "assistant" && (
                    <div className="ai-image-progress">
                      <div style={{ width: `${message.progress}%` }} />
                    </div>
                  )}
              </div>
            </div>
          ))}

          <div ref={bottomRef} />
        </div>

        {cameraOn && (
          <div className="ai-camera-box">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="ai-camera-video"
            />

            <div className="ai-camera-actions">
              <button type="button" onClick={captureImage}>
                📸 Capture
              </button>

              <button type="button" onClick={stopCamera}>
                Cancel
              </button>
            </div>

            <canvas ref={canvasRef} style={{ display: "none" }} />
          </div>
        )}

        {imageFile && imagePreviewUrl && !cameraOn && (
          <div className="ai-image-preview">
            <img
              src={imagePreviewUrl}
              alt="Selected preview"
              className="ai-preview-img"
            />

            <div className="ai-image-actions">
              <span>{imageFile.name}</span>

              <button type="button" onClick={removeSelectedImage}>
                ✕ Remove
              </button>
            </div>
          </div>
        )}

        <div className="ai-chat-suggestions">
          <button
            type="button"
            onClick={() => applySuggestion("What are the red flags in this session?")}
          >
            Red flags
          </button>

          <button
            type="button"
            onClick={() => applySuggestion("What should I ask the doctor?")}
          >
            Doctor questions
          </button>

          <button
            type="button"
            onClick={() =>
              applySuggestion("Explain this session in simple words.")
            }
          >
            Simple summary
          </button>
        </div>

        <div className="ai-chat-input-wrap">
          <textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              imageFile
                ? "Add a question about this image..."
                : "Ask about this session..."
            }
            rows={2}
            disabled={loading}
          />

          <button
            type="button"
            onClick={sendMessage}
            disabled={loading || (!input.trim() && !imageFile)}
          >
            {loading ? "..." : "Send"}
          </button>
        </div>

        <div className="ai-chat-tools">
          <button type="button" onClick={openFilePicker} title="Upload image">
            📎
          </button>

          <button type="button" onClick={startCamera} title="Camera">
            📷
          </button>

          <button
            type="button"
            onClick={startVoice}
            title="Voice input"
            className={isRecording ? "recording" : ""}
          >
            {isRecording ? "🎙️..." : "🎙️"}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept="image/*"
            onChange={handleImageUpload}
          />
        </div>
      </aside>
    </>
  );
}